import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, RotateCw } from "lucide-react";

let currentAudio = null;

export function fmtTime(s) {
  const n = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(n / 3600), m = Math.floor((n % 3600) / 60), sec = n % 60;
  const mm = h ? String(m).padStart(2, "0") : String(m);
  return `${h ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`;
}

// Inline player on a dark brass surface. Only one instance plays at a time.
export function AudioPlayer({ src, durationHint = 0, startOnMount = false, apiRef, onPlayingChange, testid = "audio-player" }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(durationHint);

  useEffect(() => {
    const a = ref.current;
    if (apiRef) apiRef.current = a;
    if (startOnMount) a.play().catch(() => {});
    return () => { a.pause(); if (currentAudio === a) currentAudio = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (v) => { setPlaying(v); if (onPlayingChange) onPlayingChange(v); };
  const onPlay = () => { const a = ref.current; if (currentAudio && currentAudio !== a) currentAudio.pause(); currentAudio = a; set(true); };
  const toggle = () => { const a = ref.current; if (a.paused) a.play().catch(() => {}); else a.pause(); };
  const jump = (d) => { const a = ref.current; a.currentTime = Math.min(Math.max(0, a.currentTime + d), dur || a.duration || 0); };

  return (
    <div className="rounded-[10px] bg-[#14110D] p-4 text-[#F4F1EA]" data-testid={testid}>
      <audio ref={ref} src={src} preload="metadata" onPlay={onPlay} onPause={() => set(false)} onEnded={() => set(false)}
        onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) setDur(e.currentTarget.duration); }} />
      <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap">
        <button onClick={() => jump(-15)} aria-label="Back 15 seconds" data-testid={`${testid}-back`} className="relative rounded-full p-1.5 text-[#C9A227] hover:bg-white/5">
          <RotateCcw className="h-5 w-5" /><span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold">15</span>
        </button>
        <button onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid={`${testid}-toggle`}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#C9A227] text-[#14110D] transition-transform hover:scale-105">
          {playing ? <Pause className="h-5 w-5" fill="currentColor" /> : <Play className="ml-0.5 h-5 w-5" fill="currentColor" />}
        </button>
        <button onClick={() => jump(30)} aria-label="Forward 30 seconds" data-testid={`${testid}-fwd`} className="relative rounded-full p-1.5 text-[#C9A227] hover:bg-white/5">
          <RotateCw className="h-5 w-5" /><span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold">30</span>
        </button>
        <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:flex-1">
          <span className="font-mono text-xs text-[#C9C2B4]" data-testid={`${testid}-elapsed`}>{fmtTime(t)}</span>
          <input type="range" min={0} max={dur || 0} step={1} value={Math.min(t, dur || 0)} aria-label="Seek"
            onChange={(e) => { ref.current.currentTime = Number(e.target.value); setT(Number(e.target.value)); }}
            data-testid={`${testid}-seek`} className="h-1 min-w-0 flex-1 cursor-pointer accent-[#C9A227]" />
          <span className="font-mono text-xs text-[#C9C2B4]" data-testid={`${testid}-total`}>{fmtTime(dur)}</span>
        </div>
      </div>
    </div>
  );
}
